import {Checkbox, openDeleteDialog, TextInput} from 'platform/components';
import {Box, Center, HStack, Icon, Text, VStack} from 'platform/foundation';

import {HTMLAttributes} from 'react';
import {useController, UseFormReturn} from 'react-hook-form';

import {DragHandle} from '@dms/features/dnd';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {
  RecordAuditDefinitionFormValue,
  RecordAuditDefinitionSection,
} from '../types/RecordAuditDefinitionFormValue';
import {TaskOperations} from '../types/TaskOperations';
import {KebabMenu, KebabMenuItem} from './KebabMenu';
import {WeightDotsControl} from './WeightDotsControl';

export interface TaskRowProps extends TestIdProps {
  formApi: UseFormReturn<RecordAuditDefinitionFormValue>;
  flatIndex: number;
  positionInSection: number;
  sectionTaskCount: number;
  sectionId: string | null;
  sections: RecordAuditDefinitionSection[];
  operations: TaskOperations;
  isDragging: boolean;
  isDimmed: boolean;
  handleProps?: HTMLAttributes<unknown>;
}

// Width of the drag-handle column. Reused as a spacer on the second line so the
// controls line up under the description input above them.
const HANDLE_COLUMN_WIDTH = 6;

// Field values fall back to defaults: during a structural array mutation a row can
// render one frame against an already-removed index before it is remounted.
export function TaskRow(props: TaskRowProps) {
  const descriptionController = useController({
    control: props.formApi.control,
    name: `tasks.${props.flatIndex}.description`,
  });
  const isMandatoryController = useController({
    control: props.formApi.control,
    name: `tasks.${props.flatIndex}.isMandatory`,
  });
  const fileRequiredController = useController({
    control: props.formApi.control,
    name: `tasks.${props.flatIndex}.fileRequired`,
  });
  const weightController = useController({
    control: props.formApi.control,
    name: `tasks.${props.flatIndex}.weight`,
  });

  const menuItems: KebabMenuItem[] = [
    {
      type: 'item',
      label: i18n.t('general.actions.moveUp'),
      isDisabled: props.positionInSection === 0,
      onClick: () => props.operations.moveUp(props.flatIndex),
    },
    {
      type: 'item',
      label: i18n.t('general.actions.moveDown'),
      isDisabled: props.positionInSection === props.sectionTaskCount - 1,
      onClick: () => props.operations.moveDown(props.flatIndex),
    },
    {type: 'divider'},
    {
      type: 'submenu',
      label: i18n.t('page.settings.generalChecklist.moveToSection'),
      // Ordered to match the on-page layout: the unsectioned block sits on top,
      // followed by the sections in their displayed order.
      items: [
        {
          label: i18n.t('page.settings.generalChecklist.noSection'),
          isDisabled: props.sectionId === null,
          isChecked: props.sectionId === null,
          onClick: () => props.operations.moveToSection(props.flatIndex, null),
        },
        ...props.sections.map((section) => ({
          label: section.name,
          isDisabled: props.sectionId === section.id,
          isChecked: props.sectionId === section.id,
          onClick: () => props.operations.moveToSection(props.flatIndex, section.id),
        })),
      ],
    },
    {
      type: 'item',
      label: i18n.t('general.actions.duplicate'),
      onClick: () => props.operations.duplicate(props.flatIndex),
    },
    {type: 'divider'},
    {
      type: 'item',
      label: i18n.t('general.actions.delete'),
      isDanger: true,
      onClick: () =>
        openDeleteDialog({
          text: i18n.t('page.settings.generalChecklist.deleteTaskConfirm'),
          onConfirm: () => props.operations.remove(props.flatIndex),
          'data-testid': suffixTestId('taskDelete', props),
        }),
    },
  ];

  return (
    <Box
      opacity={props.isDragging || props.isDimmed ? 0.4 : 1}
      padding={3}
      border="1px solid"
      borderColor="general.separator"
      borderRadius="medium"
      backgroundColor="palettes.neutral.10.100"
      data-testid={suffixTestId('taskRow', props)}
    >
      <VStack spacing={3}>
        <HStack spacing={3} align="center">
          <Center width={HANDLE_COLUMN_WIDTH}>
            <DragHandle {...props.handleProps} aria-label={i18n.t('general.actions.reorder')}>
              <Icon value="action/drag_indicator" size={4} />
            </DragHandle>
          </Center>
          <Box flex={1}>
            <TextInput
              value={descriptionController.field.value ?? ''}
              onChange={(value) => descriptionController.field.onChange(value ?? '')}
              placeholder={i18n.t('page.settings.generalChecklist.taskDescriptionPlaceholder')}
              maxLength={255}
              data-testid={suffixTestId('taskDescription', props)}
            />
          </Box>
          <KebabMenu
            items={menuItems}
            aria-label={i18n.t('page.settings.generalChecklist.taskActions')}
            data-testid={suffixTestId('taskMenu', props)}
          />
        </HStack>

        {/* Line 2: controls aligned under the description input; weight pushed right. */}
        <HStack spacing={3} align="center">
          <Box width={HANDLE_COLUMN_WIDTH} />
          <Checkbox
            value={isMandatoryController.field.value ?? false}
            onChange={isMandatoryController.field.onChange}
            label={i18n.t('entity.recordAudit.task.mandatory')}
            data-testid={suffixTestId('taskIsMandatory', props)}
          />
          <Checkbox
            value={fileRequiredController.field.value ?? false}
            onChange={fileRequiredController.field.onChange}
            label={i18n.t('entity.recordAudit.task.fileRequired')}
            data-testid={suffixTestId('taskFileRequired', props)}
          />
          <Box flex={1} />
          <HStack spacing={2} align="center">
            <Text size="small" color="secondary">
              {i18n.t('entity.recordAudit.task.weight')}
            </Text>
            <WeightDotsControl
              value={weightController.field.value ?? 1}
              onChange={weightController.field.onChange}
              data-testid={suffixTestId('taskWeight', props)}
            />
          </HStack>
        </HStack>
      </VStack>
    </Box>
  );
}
